import "./MetaDocToolbar.scss";

import Button from "components/Button/Button";
import { KaboodleToaster } from "components/common";
import { Tooltip } from "components/Tooltip/Tooltip";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import {
  type Dispatch,
  type ChangeEvent,
  type SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "utils/redux";

import { usePageNavigation } from "../../hooks/navigation/usePageNavigation";
import { usePageDeletion } from "../../hooks/page-operations/usePageDeletion";
import { usePageInput } from "../../hooks/page-operations/usePageInput";
import { usePageMovement } from "../../hooks/page-operations/usePageMovement";
import type { RunAiModelDecision } from "../../rules/runAiModelRules";
import { logMetadocDebug } from "../../utils/metadocDebugLogger";
import MovePagesModal from "../table-of-contents/MovePagesModal";
import DeletePagesModal from "./DeletePagesModal";
import MetaDocTocFilter from "./toc-filter/MetaDocTocFilter";

import type { DocumentGroup } from "services/document-group-api";
import type { MetadocSearchIndexStatus } from "services/metadoc-search-api";
import {
  getCloneReasonLabel,
  METADOC_SEARCH_DEBOUNCE_MS,
  METADOC_ZOOM_DEFAULT_PERCENT,
  type CloneReason,
} from "pages/metadoc/MetadocConstants";
import { type GroupingSource } from "services/metadoc-api";
import type { BackgroundJobDataType } from "types/DataTypes";
import type { MetaDocToolbarHandle, PageState } from "../../metadoc.types";
import { useGetOrganizationSettingsQuery } from "services/misc-api";

export type { MetaDocToolbarHandle };

type MetaDocToolbarProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  zoomPercent: number;
  onZoomPercentChange: (value: number) => void;
  selectedPages: Set<number>;
  onClearSelection: () => void;
  versionHash?: string | null;
  groupingSource?: GroupingSource;
  disabled?: boolean;
  documentGroups?: DocumentGroup[];
  onSetPageStates?: (states: Map<number, PageState>) => void;
  metadocId?: number;
  pageClassificationStats?: {
    coveredPages: number;
    totalPages: number;
    percentage: number;
  };
  metadocStatus?: string;
  onRunAIModel?: () => void;
  isRunningAIModel?: boolean;
  isRunAiModelDisabled?: boolean;
  runAiModelDisabledReason?: string;
  lastCompletedAIJob?: BackgroundJobDataType | null;
  isRunAiModelReady?: boolean;
  onSearch?: (query: string) => void;
  hasAiRun?: boolean;
  searchIndexStatus?: MetadocSearchIndexStatus;
  isSearchIndexFetching?: boolean;
  isSearchReady?: boolean;
  onStartIndexing?: () => void;
  isStartingIndexing?: boolean;
  isSearching?: boolean;
  searchResultCount?: number;
  searchNavigableResultCount?: number;
  activeSearchResultIndex?: number | null;
  onGoToPreviousSearchResult?: () => void;
  onGoToNextSearchResult?: () => void;
  canGoToPreviousSearchResult?: boolean;
  canGoToNextSearchResult?: boolean;
  lastSearchQuery?: string;
  onDeletionStateChange?: (
    isDeletingPages: boolean,
    pageCount: number,
    pageRanges: number[][],
  ) => void;
  onMovementStateChange?: (
    isMovingPages: boolean,
    pageCount: number,
    pageRanges: number[][],
    targetPosition: number,
  ) => void;
  onMovementOperationStart?: (meta: {
    pageCount: number;
    pageRanges: number[][];
    targetPosition: number;
  }) => void;
  onMovementOperationJobCreated?: (jobId: number) => void;
  onMovementOperationFail?: (statusMessage?: string) => void;
  onMovementOperationReset?: () => void;
  onDeletionOperationStart?: (meta: {
    pageCount: number;
    pageRanges: number[][];
    cursorPositionBeforeDeletion: number;
  }) => void;
  onDeletionOperationJobCreated?: (jobId: number) => void;
  onDeletionOperationFail?: (statusMessage?: string) => void;
  onDeletionOperationReset?: () => void;
  onMoveTargetSelectionChange?: (
    isSelecting: boolean,
    onTargetSelect: ((targetPosition: number) => void) | null,
    onCancel?: (() => void) | null,
  ) => void;
  isPdfLoading?: boolean;
  isMetadocFetching?: boolean;
  isOpsPostAiReadOnly?: boolean;
  runAiModelDecision?: RunAiModelDecision;
  cloneReason?: CloneReason | null;
  availableTocDocumentTypes?: string[];
  selectedTocDocumentTypes?: string[];
  onTocDocumentTypesChange?: Dispatch<SetStateAction<string[]>>;
  tocSearchQuery?: string;
  onTocSearchQueryChange?: (value: string) => void;
  tocPageRangeStart?: number | null;
  tocPageRangeEnd?: number | null;
  onTocPageRangeChange?: (value: {
    startPage: number | null;
    endPage: number | null;
  }) => void;
  tocDateRangeStart?: string | null;
  tocDateRangeEnd?: string | null;
  onTocDateRangeChange?: (value: {
    startDate: string | null;
    endDate: string | null;
  }) => void;
  visiblePageRangeStart?: number;
  visiblePageRangeEnd?: number;
  visiblePageNumbers?: number[];
  hideFilteredPages?: boolean;
  onHideFilteredPagesChange?: (value: boolean) => void;
  summarySearchQuery?: string;
  onSummarySearchQueryChange?: (value: string) => void;
  onSummarySearchSubmit?: () => void;
  onSummarySearchClear?: () => void;
  isSummarySearching?: boolean;
  summarySearchResultCount?: number;
  lastSummarySearchQuery?: string;
  summarySearchNavigableResultCount?: number;
  onGoToPreviousSummarySearchResult?: () => void;
  onGoToNextSummarySearchResult?: () => void;
  medicationSearchQuery?: string;
  onMedicationSearchQueryChange?: (value: string) => void;
  onMedicationSearchClear?: () => void;
  medicationSearchResultCount?: number;
  lastMedicationSearchQuery?: string;
  medicationSearchNavigableResultCount?: number;
  onGoToPreviousMedicationSearchResult?: () => void;
  onGoToNextMedicationSearchResult?: () => void;
  codeSearchQuery?: string;
  onCodeSearchQueryChange?: (value: string) => void;
  onCodeSearchClear?: () => void;
  codeSearchResultCount?: number;
  lastCodeSearchQuery?: string;
  codeSearchNavigableResultCount?: number;
  onGoToPreviousCodeSearchResult?: () => void;
  onGoToNextCodeSearchResult?: () => void;
};

const ZOOM_MIN_PERCENT = 90;
const ZOOM_MAX_PERCENT = 300;
const ZOOM_STEP_PERCENT = 10;
const ZOOM_PRESET_PERCENTAGES = [
  90, 100, 110, 125, 150, 175, 200, 225, 250, 275, 300,
];

const clampZoomPercent = (value: number): number => {
  return Math.min(Math.max(value, ZOOM_MIN_PERCENT), ZOOM_MAX_PERCENT);
};

const getNextZoomPercent = (
  currentPercent: number,
  direction: "in" | "out",
): number => {
  if (direction === "in") {
    const nextPreset = ZOOM_PRESET_PERCENTAGES.find(
      (preset) => preset > currentPercent,
    );
    if (nextPreset) return nextPreset;
    return clampZoomPercent(currentPercent + ZOOM_STEP_PERCENT);
  }

  const previousPreset = [...ZOOM_PRESET_PERCENTAGES]
    .reverse()
    .find((preset) => preset < currentPercent);
  if (previousPreset) return previousPreset;
  return clampZoomPercent(currentPercent - ZOOM_STEP_PERCENT);
};

const MetaDocToolbar = forwardRef<MetaDocToolbarHandle, MetaDocToolbarProps>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      zoomPercent,
      onZoomPercentChange,
      selectedPages,
      onClearSelection,
      versionHash,
      groupingSource = "OPS",
      disabled = false,
      documentGroups = [],
      metadocId,
      onSetPageStates,
      onRunAIModel,
      isRunningAIModel = false,
      isRunAiModelDisabled = false,
      runAiModelDisabledReason,
      lastCompletedAIJob,
      isRunAiModelReady = false,
      onSearch,
      hasAiRun = false,
      searchIndexStatus,
      isSearchIndexFetching = false,
      isSearchReady = false,
      onStartIndexing,
      isStartingIndexing = false,
      isSearching = false,
      searchResultCount = 0,
      searchNavigableResultCount = 0,
      activeSearchResultIndex = null,
      onGoToPreviousSearchResult,
      onGoToNextSearchResult,
      canGoToPreviousSearchResult = false,
      canGoToNextSearchResult = false,
      lastSearchQuery = "",
      onDeletionStateChange,
      onMovementStateChange,
      onMovementOperationStart,
      onMovementOperationJobCreated,
      onMovementOperationFail,
      onMovementOperationReset,
      onDeletionOperationStart,
      onDeletionOperationJobCreated,
      onDeletionOperationFail,
      onDeletionOperationReset,
      onMoveTargetSelectionChange,
      isPdfLoading = false,
      isMetadocFetching = false,
      isOpsPostAiReadOnly = false,
      runAiModelDecision,
      cloneReason = null,
      availableTocDocumentTypes = [],
      selectedTocDocumentTypes = [],
      onTocDocumentTypesChange,
      tocSearchQuery = "",
      onTocSearchQueryChange,
      tocPageRangeStart = null,
      tocPageRangeEnd = null,
      onTocPageRangeChange,
      tocDateRangeStart = null,
      tocDateRangeEnd = null,
      onTocDateRangeChange,
      visiblePageRangeStart = 1,
      visiblePageRangeEnd = totalPages,
      visiblePageNumbers,
      hideFilteredPages = false,
      onHideFilteredPagesChange,
      summarySearchQuery = "",
      onSummarySearchQueryChange,
      onSummarySearchSubmit,
      onSummarySearchClear,
      isSummarySearching = false,
      summarySearchResultCount = 0,
      lastSummarySearchQuery = "",
      summarySearchNavigableResultCount = 0,
      onGoToPreviousSummarySearchResult,
      onGoToNextSummarySearchResult,
      medicationSearchQuery = "",
      onMedicationSearchQueryChange,
      onMedicationSearchClear,
      medicationSearchResultCount = 0,
      lastMedicationSearchQuery = "",
      medicationSearchNavigableResultCount = 0,
      onGoToPreviousMedicationSearchResult,
      onGoToNextMedicationSearchResult,
      codeSearchQuery = "",
      onCodeSearchQueryChange,
      onCodeSearchClear,
      codeSearchResultCount = 0,
      lastCodeSearchQuery = "",
      codeSearchNavigableResultCount = 0,
      onGoToPreviousCodeSearchResult,
      onGoToNextCodeSearchResult,
    },
    ref,
  ) => {
    const constants = useSelector((state) => state.shared.constants);
    const { data: organizationSettings } = useGetOrganizationSettingsQuery();
    const isMetadocIndexingEnabled =
      organizationSettings?.metadoc_indexing_enabled !== false;

    const isMoveEnabled =
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_MOVE_ENABLED] &&
      groupingSource !== "AI";
    const isDeleteEnabled =
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_DELETE_ENABLED];
    const isOpsSearchEnabled =
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_OPS_SEARCH_ENABLED];
    const isSearchEnabled =
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_SEARCH_ENABLED] &&
      (hasAiRun || (groupingSource !== "AI" && isOpsSearchEnabled));
    const isFilterEnabled =
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_FILTER_ENABLED];
    const isZoomEnabled =
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_ZOOM_ENABLED];

    // page deletion hook
    const deletion = usePageDeletion({
      metadocId,
      versionHash,
      groupingSource,
      currentPage,
      selectedPages,
      onClearSelection,
      onSetPageStates,
      onDeletionStateChange,
      onDeletionOperationStart,
      onDeletionOperationJobCreated,
      onDeletionOperationFail,
      onDeletionOperationReset,
    });

    // page movement hook
    const movement = usePageMovement({
      metadocId,
      versionHash,
      groupingSource,
      selectedPages,
      onClearSelection,
      onSetPageStates,
      onMovementStateChange,
      onMovementOperationStart,
      onMovementOperationJobCreated,
      onMovementOperationFail,
      onMovementOperationReset,
    });

    // notify parent when target selection state changes
    useEffect(() => {
      if (onMoveTargetSelectionChange) {
        onMoveTargetSelectionChange(
          movement.isSelectingMoveTarget,
          movement.isSelectingMoveTarget ? movement.handleTargetSelect : null,
          movement.isSelectingMoveTarget
            ? movement.cancelTargetSelection
            : null,
        );
      }
    }, [
      movement.isSelectingMoveTarget,
      movement.handleTargetSelect,
      movement.cancelTargetSelection,
      onMoveTargetSelectionChange,
    ]);

    // page navigation hook
    const navigation = usePageNavigation({
      currentPage,
      totalPages,
      onPageChange,
      minPage: visiblePageRangeStart,
      maxPage: visiblePageRangeEnd,
      visiblePageNumbers,
    });

    // page input hook
    const pageInput = usePageInput({
      currentPage,
      totalPages,
      onPageChange,
      onPageJump: onClearSelection,
      minPage: visiblePageRangeStart,
      maxPage: visiblePageRangeEnd,
      visiblePageNumbers,
    });

    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const wasDeletingPagesRef = useRef<boolean>(false);
    const totalPagesBeforeDeletionRef = useRef<number | null>(null);

    // compute if we're in a deleting or moving state (disable all interactions)
    const isDisabled =
      disabled || deletion.isDeletingPages || movement.isMovingPages;
    const canEditPagesInOpsReadonlyMode =
      isOpsPostAiReadOnly && Boolean(cloneReason);
    const shouldHidePageEditButtons =
      isOpsPostAiReadOnly && !canEditPagesInOpsReadonlyMode;
    const isPageEditActionDisabled =
      isDisabled || (isOpsPostAiReadOnly && !canEditPagesInOpsReadonlyMode);
    const isAnalystEntireGroupDeleteSelected = useMemo(() => {
      if (groupingSource !== "AI" || selectedPages.size === 0) {
        return false;
      }

      return documentGroups.some((group) => {
        for (let page = group.start_pg; page <= group.end_pg; page += 1) {
          if (!selectedPages.has(page)) {
            return false;
          }
        }

        return true;
      });
    }, [groupingSource, selectedPages, documentGroups]);
    const analystDeleteDisabledReason = isAnalystEntireGroupDeleteSelected
      ? "To delete entire group pages, delete from the table of contents."
      : null;
    const isDeleteActionDisabled =
      isPageEditActionDisabled ||
      selectedPages.size === 0 ||
      movement.isSelectingMoveTarget ||
      isAnalystEntireGroupDeleteSelected;
    const isDeleteBaseDisabled =
      isPageEditActionDisabled ||
      selectedPages.size === 0 ||
      movement.isSelectingMoveTarget;
    const shouldShowRunAIButton =
      runAiModelDecision?.showRunAiModelButton ??
      (!isOpsPostAiReadOnly && groupingSource !== "AI");
    const isSearchDisabled =
      isDisabled ||
      movement.isSelectingMoveTarget ||
      !isSearchReady ||
      !isSearchEnabled;
    const isSearchToggleDisabled =
      isDisabled ||
      movement.isSelectingMoveTarget ||
      !isSearchEnabled ||
      !isSearchReady;
    const hasSearchResults = Boolean(lastSearchQuery) && searchResultCount > 0;
    const hasNavigableSearchResults = searchNavigableResultCount > 0;
    const hasMultipleNavigableSearchResults = searchNavigableResultCount > 1;

    useEffect(() => {
      if (isSearchEnabled && isSearchExpanded) {
        searchInputRef.current?.focus();
      }
    }, [isSearchEnabled, isSearchExpanded]);

    useEffect(() => {
      if (!isSearchEnabled && isSearchExpanded) {
        setIsSearchExpanded(false);
      }
    }, [isSearchEnabled, isSearchExpanded]);

    useEffect(() => {
      if (deletion.isDeletingPages && !wasDeletingPagesRef.current) {
        totalPagesBeforeDeletionRef.current = totalPages;
        logMetadocDebug("toolbar delete flow started", {
          metadocId,
          totalPages,
          selectedPageCount: selectedPages.size,
        });
      }

      if (!deletion.isDeletingPages && wasDeletingPagesRef.current) {
        logMetadocDebug("toolbar delete flow settled", {
          metadocId,
          totalPagesBeforeDeletion: totalPagesBeforeDeletionRef.current,
          totalPagesAfterDeletion: totalPages,
          currentPage,
          isMetadocFetching,
          isPdfLoading,
          documentGroupCount: documentGroups.length,
          documentGroupRanges: documentGroups.map((group) => ({
            id: group.id,
            startPage: group.start_pg,
            endPage: group.end_pg,
          })),
        });
        totalPagesBeforeDeletionRef.current = null;
      }

      wasDeletingPagesRef.current = deletion.isDeletingPages;
    }, [
      deletion.isDeletingPages,
      metadocId,
      totalPages,
      selectedPages,
      currentPage,
      isMetadocFetching,
      isPdfLoading,
      documentGroups,
    ]);

    const closeSearchPanel = useCallback(() => {
      setIsSearchExpanded(false);
      setSearchQuery("");
      onSearch?.("");
    }, [onSearch]);
    // keyboard shortcut: "D" to delete selected pages (only when feature enabled)
    useHotkeys(
      "d",
      (e) => {
        if (
          isDeleteEnabled &&
          selectedPages.size > 0 &&
          !isPageEditActionDisabled &&
          isAnalystEntireGroupDeleteSelected &&
          analystDeleteDisabledReason
        ) {
          e.preventDefault();
          KaboodleToaster.error(analystDeleteDisabledReason);
          return;
        }

        if (
          isDeleteEnabled &&
          selectedPages.size > 0 &&
          !isPageEditActionDisabled &&
          !isAnalystEntireGroupDeleteSelected &&
          metadocId &&
          versionHash
        ) {
          e.preventDefault();
          deletion.openDeleteModal();
        }
      },
      {
        enableOnFormTags: false, // don't trigger when typing in inputs
        preventDefault: true,
      },
      [
        isDeleteEnabled,
        selectedPages.size,
        isPageEditActionDisabled,
        isAnalystEntireGroupDeleteSelected,
        analystDeleteDisabledReason,
        metadocId,
        versionHash,
      ],
    );

    // keyboard shortcut: "M" to move selected pages (only when feature enabled)
    useHotkeys(
      "m",
      (e) => {
        if (
          isMoveEnabled &&
          selectedPages.size > 0 &&
          !isPageEditActionDisabled &&
          metadocId &&
          versionHash
        ) {
          e.preventDefault();
          movement.openMoveModal();
        }
      },
      {
        enableOnFormTags: false, // don't trigger when typing in inputs
        preventDefault: true,
      },
      [
        isMoveEnabled,
        selectedPages.size,
        isPageEditActionDisabled,
        metadocId,
        versionHash,
      ],
    );

    // keyboard shortcut: "Esc" to cancel target selection
    useHotkeys(
      "escape",
      (e) => {
        if (movement.isSelectingMoveTarget) {
          e.preventDefault();
          movement.cancelTargetSelection();
          return;
        }

        if (isSearchExpanded) {
          e.preventDefault();
          closeSearchPanel();
          return;
        }
      },
      {
        enableOnFormTags: false,
        preventDefault: true,
      },
      [
        movement.isSelectingMoveTarget,
        movement.cancelTargetSelection,
        closeSearchPanel,
        isSearchExpanded,
      ],
    );

    // keyboard shortcut: "Ctrl+F" to open and focus search
    useHotkeys(
      "ctrl+f",
      (e) => {
        if (!isSearchEnabled || isSearchToggleDisabled) {
          return;
        }

        e.preventDefault();
        setIsSearchExpanded(true);
        searchInputRef.current?.focus();
      },
      {
        enableOnFormTags: false,
        preventDefault: true,
      },
      [isSearchEnabled, isSearchToggleDisabled],
    );
    const handleClearSelection = () => {
      onClearSelection();
    };

    const handleSearch = (query: string = searchQuery) => {
      if (!isSearchEnabled) {
        return;
      }
      const trimmedQuery = query.trim();
      if (!trimmedQuery) {
        return;
      }
      onSearch?.(trimmedQuery);
    };
    const isZoomDisabled = isDisabled || movement.isSelectingMoveTarget;

    const handleZoomOut = () => {
      onZoomPercentChange(getNextZoomPercent(zoomPercent, "out"));
    };

    const handleZoomIn = () => {
      onZoomPercentChange(getNextZoomPercent(zoomPercent, "in"));
    };

    const handleZoomReset = () => {
      onZoomPercentChange(METADOC_ZOOM_DEFAULT_PERCENT);
    };

    const handleZoomPresetChange = (event: ChangeEvent<HTMLSelectElement>) => {
      const nextZoom = Number(event.target.value);
      if (Number.isNaN(nextZoom)) {
        return;
      }
      onZoomPercentChange(clampZoomPercent(nextZoom));
    };
    // keyboard shortcut: "Ctrl+R" to reset zoom
    useHotkeys(
      "ctrl+r",
      (e) => {
        if (!isZoomEnabled || isZoomDisabled) {
          return;
        }

        e.preventDefault();
        handleZoomReset();
      },
      {
        enableOnFormTags: false,
        preventDefault: true,
      },
      [isZoomEnabled, isZoomDisabled, handleZoomReset],
    );

    // expose page input and zoom controls to parent keyboard shortcuts
    useImperativeHandle(ref, () => ({
      focusPageInput: pageInput.focusPageInput,
      ...(isZoomEnabled && {
        zoomIn: handleZoomIn,
        zoomOut: handleZoomOut,
      }),
    }));

    const navigateSearchResults = useCallback(
      (shouldGoToPrevious: boolean): boolean => {
        if (!isSearchExpanded || isSearchDisabled) {
          return false;
        }

        const trimmedQuery = searchQuery.trim();
        const isActiveSearchQuery =
          trimmedQuery.length > 0 && trimmedQuery === lastSearchQuery;

        if (!isActiveSearchQuery || !hasMultipleNavigableSearchResults) {
          return false;
        }

        if (shouldGoToPrevious) {
          onGoToPreviousSearchResult?.();
          return true;
        }

        onGoToNextSearchResult?.();
        return true;
      },
      [
        hasMultipleNavigableSearchResults,
        isSearchDisabled,
        isSearchExpanded,
        lastSearchQuery,
        onGoToNextSearchResult,
        onGoToPreviousSearchResult,
        searchQuery,
      ],
    );

    // keyboard shortcuts: "Enter"/"Shift+Enter" to traverse search results
    useHotkeys(
      "enter",
      (event) => {
        if (navigateSearchResults(false)) {
          event.preventDefault();
        }
      },
      {
        enableOnFormTags: false,
        preventDefault: false,
      },
      [navigateSearchResults],
    );

    useHotkeys(
      "shift+enter",
      (event) => {
        if (navigateSearchResults(true)) {
          event.preventDefault();
        }
      },
      {
        enableOnFormTags: false,
        preventDefault: false,
      },
      [navigateSearchResults],
    );

    useEffect(() => {
      if (!isSearchExpanded || isSearchDisabled || isSearching) {
        return;
      }

      const trimmedQuery = searchQuery.trim();
      if (!trimmedQuery || trimmedQuery === lastSearchQuery) {
        return;
      }

      const timeoutId = window.setTimeout(() => {
        onSearch?.(trimmedQuery);
      }, METADOC_SEARCH_DEBOUNCE_MS);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [
      isSearchDisabled,
      isSearchExpanded,
      isSearching,
      lastSearchQuery,
      onSearch,
      searchQuery,
    ]);

    useEffect(() => {
      if (isDisabled || isSummarySearching) {
        return;
      }

      const trimmedQuery = summarySearchQuery.trim();
      if (!trimmedQuery || trimmedQuery === lastSummarySearchQuery) {
        return;
      }

      const timeoutId = window.setTimeout(() => {
        onSummarySearchSubmit?.();
      }, METADOC_SEARCH_DEBOUNCE_MS);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [
      isDisabled,
      isSummarySearching,
      lastSummarySearchQuery,
      onSummarySearchSubmit,
      summarySearchQuery,
    ]);

    const getSearchStatusLabel = () => {
      if (isSearchIndexFetching) return "Checking index...";
      if (groupingSource === "OPS" && hasAiRun && !isSearchReady) {
        return "Ops search unavailable: not indexed before AI ran";
      }
      if (!searchIndexStatus) return "Index not available";
      if (searchIndexStatus.status === "not_indexed") {
        return "Indexing required for search";
      }
      if (searchIndexStatus.status === "pending") {
        return "Indexing queued...";
      }
      if (searchIndexStatus.status === "processing") {
        return "Indexing...";
      }
      if (searchIndexStatus.status === "failed") {
        return "Index failed";
      }
      if (searchIndexStatus.status === "invalidated") {
        return "Pages changed since indexing; re-index to search";
      }
      if (
        searchIndexStatus.status === "completed" &&
        !searchIndexStatus.is_searchable
      ) {
        return "Index not ready";
      }
      return "Search ready";
    };

    const isIndexingInProgress =
      isSearchIndexFetching ||
      isStartingIndexing ||
      searchIndexStatus?.status === "processing" ||
      searchIndexStatus?.status === "pending";
    const showReindexButton =
      isSearchEnabled &&
      isMetadocIndexingEnabled &&
      !isSearchReady &&
      Boolean(onStartIndexing) &&
      !(groupingSource === "OPS" && hasAiRun);
    const cloneReasonLabel = getCloneReasonLabel(cloneReason);

    return (
      <>
        <DeletePagesModal
          isOpen={deletion.isDeleteModalOpen}
          onClose={deletion.closeDeleteModal}
          onConfirm={deletion.handleDeleteConfirm}
          selectedPages={selectedPages}
          totalPages={totalPages}
          documentGroups={documentGroups}
          groupingSource={groupingSource}
        />
        <MovePagesModal
          isOpen={movement.isMoveModalOpen}
          onClose={movement.closeMoveModal}
          onConfirm={movement.handleMoveConfirm}
          onSelectPosition={movement.startTargetSelectionFromModal}
          selectedPages={selectedPages}
          totalPages={totalPages}
          documentGroups={documentGroups}
          isMoving={movement.isMovingPages}
          preselectedTarget={movement.selectedMoveTarget}
        />
        <div className="metadoc-toolbar-shell">
          <div className="metadoc-toolbar">
            <div className="metadoc-toolbar__section metadoc-toolbar__grouping"></div>

            {onRunAIModel && (
              <div className="metadoc-toolbar__section metadoc-toolbar__ai-actions">
                {shouldShowRunAIButton ? (
                  <>
                    <span className="metadoc-toolbar__section-label">
                      AI Actions
                    </span>
                    <div className="metadoc-ai-actions-wrapper">
                      {isRunAiModelDisabled && runAiModelDisabledReason ? (
                        <Tooltip
                          content={runAiModelDisabledReason}
                          place="bottom"
                          positionStrategy="fixed"
                        >
                          <Button
                            className={`metadoc-action-btn ${
                              isRunAiModelReady && !isRunAiModelDisabled
                                ? "metadoc-action-btn--ready"
                                : ""
                            }`}
                            onClick={onRunAIModel}
                            disabled={
                              isRunAiModelDisabled ||
                              movement.isSelectingMoveTarget
                            }
                          >
                            <span className="material-icons">
                              {isRunningAIModel
                                ? "hourglass_empty"
                                : "auto_awesome"}
                            </span>
                            RUN AI MODEL
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          className={`metadoc-action-btn ${
                            isRunAiModelReady && !isRunAiModelDisabled
                              ? "metadoc-action-btn--ready"
                              : ""
                          }`}
                          onClick={onRunAIModel}
                          disabled={
                            isRunAiModelDisabled ||
                            movement.isSelectingMoveTarget
                          }
                        >
                          <span className="material-icons">
                            {isRunningAIModel
                              ? "hourglass_empty"
                              : "auto_awesome"}
                          </span>
                          RUN AI MODEL
                        </Button>
                      )}
                      {lastCompletedAIJob && (
                        <div className="metadoc-action-status metadoc-action-status--completed">
                          <span className="material-icons metadoc-action-status__icon">
                            check_circle
                          </span>
                          <span className="metadoc-action-status__text">
                            {new Date(
                              lastCompletedAIJob.created,
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  (lastCompletedAIJob || cloneReasonLabel) && (
                    <div className="metadoc-ai-actions-wrapper">
                      <div
                        className="metadoc-action-status metadoc-action-status--completed"
                        style={{
                          position: "static",
                          left: 0,
                        }}
                      >
                        <span className="material-icons metadoc-action-status__icon">
                          check_circle
                        </span>
                        <span className="metadoc-action-status__text">
                          {cloneReasonLabel
                            ? `Clone Reason: ${cloneReasonLabel}`
                            : `AI Analysis Completed: ${new Date(
                                lastCompletedAIJob?.created ?? "",
                              ).toLocaleString([], {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`}
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            <div className="metadoc-toolbar__section metadoc-toolbar__clear-selection">
              {!shouldHidePageEditButtons ? (
                <>
                  <button
                    onClick={handleClearSelection}
                    className="metadoc-toolbar__clear-button"
                    title="Clear selection"
                    disabled={
                      isDisabled ||
                      selectedPages.size === 0 ||
                      movement.isSelectingMoveTarget
                    }
                  >
                    <span className="material-icons">close</span>
                    Clear
                    <span className="metadoc-toolbar__nav-button-shortcut">
                      ESC
                    </span>
                  </button>
                  {isMoveEnabled && (
                    <button
                      onClick={movement.openMoveModal}
                      className={`metadoc-toolbar__clear-button metadoc-toolbar__move-button${movement.isSelectingMoveTarget ? " metadoc-toolbar__move-button--active" : ""}`}
                      title="Move selected pages"
                      disabled={
                        isPageEditActionDisabled || selectedPages.size === 0
                      }
                    >
                      <span className="material-icons">drive_file_move</span>
                      Move
                      <span className="metadoc-toolbar__nav-button-shortcut">
                        M
                      </span>
                    </button>
                  )}
                  {isDeleteEnabled && (
                    <>
                      {analystDeleteDisabledReason ? (
                        <Tooltip
                          content={analystDeleteDisabledReason}
                          positionStrategy="fixed"
                        >
                          <button
                            onClick={() => {
                              if (!isAnalystEntireGroupDeleteSelected) {
                                deletion.openDeleteModal();
                              }
                            }}
                            className="metadoc-toolbar__clear-button metadoc-toolbar__delete-button"
                            title="Delete selected pages"
                            disabled={isDeleteBaseDisabled}
                            aria-disabled={isAnalystEntireGroupDeleteSelected}
                          >
                            <span className="material-icons">delete</span>
                            Delete
                            <span className="metadoc-toolbar__nav-button-shortcut">
                              D
                            </span>
                          </button>
                        </Tooltip>
                      ) : (
                        <button
                          onClick={deletion.openDeleteModal}
                          className="metadoc-toolbar__clear-button metadoc-toolbar__delete-button"
                          title="Delete selected pages"
                          disabled={isDeleteActionDisabled}
                        >
                          <span className="material-icons">delete</span>
                          Delete
                          <span className="metadoc-toolbar__nav-button-shortcut">
                            D
                          </span>
                        </button>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div
                  className="metadoc-toolbar__clear-selection-placeholder"
                  aria-hidden="true"
                />
              )}
            </div>

            <div className="metadoc-toolbar__section metadoc-toolbar__navigation">
              {/* <span className="metadoc-toolbar__section-label">View</span> */}
              {isZoomEnabled && (
                <div className="metadoc-toolbar__navigation-zoom">
                  <div className="metadoc-toolbar__zoom">
                    <button
                      type="button"
                      onClick={handleZoomOut}
                      className="metadoc-toolbar__nav-button metadoc-toolbar__zoom-button"
                      title="Zoom out"
                      aria-label="Zoom out"
                      disabled={
                        isZoomDisabled || zoomPercent <= ZOOM_MIN_PERCENT
                      }
                    >
                      <span className="material-icons">remove</span>
                      <small className="metadoc-toolbar__nav-button-shortcut">
                        ⌃-
                      </small>
                    </button>
                    <select
                      value={zoomPercent}
                      onChange={handleZoomPresetChange}
                      className="metadoc-toolbar__zoom-select"
                      aria-label="Zoom percentage"
                      disabled={isZoomDisabled}
                    >
                      {ZOOM_PRESET_PERCENTAGES.map((preset) => (
                        <option key={preset} value={preset}>
                          {preset}%
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleZoomIn}
                      className="metadoc-toolbar__nav-button metadoc-toolbar__zoom-button"
                      title="Zoom in"
                      aria-label="Zoom in"
                      disabled={
                        isZoomDisabled || zoomPercent >= ZOOM_MAX_PERCENT
                      }
                    >
                      <span className="material-icons">add</span>
                      <small className="metadoc-toolbar__nav-button-shortcut">
                        ⌃+
                      </small>
                    </button>
                    <button
                      type="button"
                      onClick={handleZoomReset}
                      className="metadoc-toolbar__nav-button metadoc-toolbar__zoom-button"
                      title="Reset zoom"
                      aria-label="Reset zoom"
                      disabled={
                        isZoomDisabled ||
                        zoomPercent === METADOC_ZOOM_DEFAULT_PERCENT
                      }
                    >
                      <span className="material-icons">restart_alt</span>
                      <small className="metadoc-toolbar__nav-button-shortcut">
                        ⌃R
                      </small>
                    </button>
                  </div>
                </div>
              )}
              <div className="metadoc-toolbar__navigation-controls">
                <button
                  onClick={navigation.goToFirstPage}
                  disabled={isDisabled || !navigation.canGoBack}
                  className="metadoc-toolbar__nav-button metadoc-toolbar__nav-button--edge"
                  title="Go to first page"
                >
                  <span className="material-icons">vertical_align_top</span>
                  <span className="metadoc-toolbar__nav-button-shortcut">
                    ←
                  </span>
                </button>
                <button
                  onClick={navigation.goBack10Pages}
                  disabled={isDisabled || !navigation.canGoBack}
                  className="metadoc-toolbar__nav-button metadoc-toolbar__nav-button--jump"
                  title="Go back 10 pages"
                >
                  <span className="material-icons">
                    keyboard_double_arrow_up
                  </span>
                  <span className="metadoc-toolbar__nav-button-label">-10</span>
                  <span className="metadoc-toolbar__nav-button-shortcut">
                    ⇧↑
                  </span>
                </button>
                <button
                  onClick={navigation.goToPreviousPage}
                  disabled={isDisabled || !navigation.canGoBack}
                  className="metadoc-toolbar__nav-button"
                  title="Previous page"
                >
                  <span className="material-icons">keyboard_arrow_up</span>
                  <span className="metadoc-toolbar__nav-button-shortcut">
                    ↑
                  </span>
                </button>
                <div className="metadoc-toolbar__page-info">
                  <span>Page </span>
                  <input
                    ref={pageInput.inputRef}
                    type="text"
                    value={
                      pageInput.isEditing ? pageInput.pageInput : currentPage
                    }
                    onChange={pageInput.handlers.onChange}
                    onFocus={pageInput.handlers.onFocus}
                    onKeyDown={pageInput.handlers.onKeyDown}
                    onBlur={pageInput.handlers.onBlur}
                    className="metadoc-toolbar__page-input"
                    title="Enter page number"
                    aria-label="Go to page"
                    disabled={disabled}
                  />
                  <span> of {totalPages}</span>
                </div>
                <button
                  onClick={navigation.goToNextPage}
                  disabled={isDisabled || !navigation.canGoForward}
                  className="metadoc-toolbar__nav-button"
                  title="Next page"
                >
                  <span className="material-icons">keyboard_arrow_down</span>
                  <span className="metadoc-toolbar__nav-button-shortcut">
                    ↓
                  </span>
                </button>
                <button
                  onClick={navigation.goForward10Pages}
                  disabled={isDisabled || !navigation.canGoForward}
                  className="metadoc-toolbar__nav-button metadoc-toolbar__nav-button--jump"
                  title="Go forward 10 pages"
                >
                  <span className="metadoc-toolbar__nav-button-label">+10</span>
                  <span className="material-icons">
                    keyboard_double_arrow_down
                  </span>
                  <span className="metadoc-toolbar__nav-button-shortcut">
                    ⇧↓
                  </span>
                </button>
                <button
                  onClick={navigation.goToLastPage}
                  disabled={isDisabled || !navigation.canGoForward}
                  className="metadoc-toolbar__nav-button metadoc-toolbar__nav-button--edge"
                  title="Go to last page"
                >
                  <span className="material-icons">vertical_align_bottom</span>
                  <span className="metadoc-toolbar__nav-button-shortcut">
                    →
                  </span>
                </button>
              </div>
              <div className="metadoc-toolbar__navigation-search">
                {isSearchEnabled &&
                  (showReindexButton ? (
                    <Tooltip
                      content={getSearchStatusLabel()}
                      place="bottom"
                      positionStrategy="fixed"
                    >
                      <button
                        type="button"
                        onClick={onStartIndexing}
                        className="metadoc-toolbar__nav-button metadoc-toolbar__nav-button--edge metadoc-toolbar__search-toggle"
                        title={
                          isIndexingInProgress ? "Indexing..." : "Re-index"
                        }
                        disabled={
                          isDisabled ||
                          movement.isSelectingMoveTarget ||
                          isIndexingInProgress
                        }
                        aria-label={
                          isIndexingInProgress ? "Indexing..." : "Re-index"
                        }
                      >
                        <span
                          className={`material-icons${isIndexingInProgress ? " metadoc-toolbar__search-index-icon--spinning" : ""}`}
                        >
                          autorenew
                        </span>
                        {isIndexingInProgress ? "Indexing..." : "Re-index"}
                      </button>
                    </Tooltip>
                  ) : isSearchReady || isMetadocIndexingEnabled ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (isSearchExpanded) {
                          closeSearchPanel();
                          return;
                        }

                        setIsSearchExpanded(true);
                      }}
                      className="metadoc-toolbar__nav-button metadoc-toolbar__nav-button--edge metadoc-toolbar__search-toggle"
                      title={
                        isSearchToggleDisabled
                          ? getSearchStatusLabel()
                          : "Search metadoc content"
                      }
                      disabled={isSearchToggleDisabled}
                      aria-expanded={isSearchExpanded}
                      aria-controls="metadoc-toolbar-search-panel"
                    >
                      <span className="material-icons">search</span>
                      Search
                      <span className="metadoc-toolbar__nav-button-shortcut">
                        ⌃F
                      </span>
                    </button>
                  ) : null)}
                {groupingSource === "AI" && isFilterEnabled && (
                  <MetaDocTocFilter
                    availableDocumentTypes={availableTocDocumentTypes}
                    selectedDocumentTypes={selectedTocDocumentTypes}
                    onDocumentTypesChange={onTocDocumentTypesChange}
                    searchQuery={tocSearchQuery}
                    onSearchQueryChange={onTocSearchQueryChange}
                    pageRangeStart={tocPageRangeStart}
                    pageRangeEnd={tocPageRangeEnd}
                    onPageRangeChange={onTocPageRangeChange}
                    dateRangeStart={tocDateRangeStart}
                    dateRangeEnd={tocDateRangeEnd}
                    onDateRangeChange={onTocDateRangeChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    hideFilteredPages={hideFilteredPages}
                    onHideFilteredPagesChange={onHideFilteredPagesChange}
                    summarySearchQuery={summarySearchQuery}
                    onSummarySearchQueryChange={onSummarySearchQueryChange}
                    onSummarySearchSubmit={onSummarySearchSubmit}
                    onSummarySearchClear={onSummarySearchClear}
                    isSummarySearching={isSummarySearching}
                    summarySearchResultCount={summarySearchResultCount}
                    onGoToPreviousSummarySearchResult={
                      onGoToPreviousSummarySearchResult
                    }
                    onGoToNextSummarySearchResult={
                      onGoToNextSummarySearchResult
                    }
                    summarySearchNavigableResultCount={
                      summarySearchNavigableResultCount
                    }
                    lastSummarySearchQuery={lastSummarySearchQuery}
                    medicationSearchQuery={medicationSearchQuery}
                    onMedicationSearchQueryChange={
                      onMedicationSearchQueryChange
                    }
                    onMedicationSearchClear={onMedicationSearchClear}
                    medicationSearchResultCount={medicationSearchResultCount}
                    medicationSearchNavigableResultCount={
                      medicationSearchNavigableResultCount
                    }
                    lastMedicationSearchQuery={lastMedicationSearchQuery}
                    onGoToPreviousMedicationSearchResult={
                      onGoToPreviousMedicationSearchResult
                    }
                    onGoToNextMedicationSearchResult={
                      onGoToNextMedicationSearchResult
                    }
                    codeSearchQuery={codeSearchQuery}
                    onCodeSearchQueryChange={onCodeSearchQueryChange}
                    onCodeSearchClear={onCodeSearchClear}
                    codeSearchResultCount={codeSearchResultCount}
                    codeSearchNavigableResultCount={
                      codeSearchNavigableResultCount
                    }
                    lastCodeSearchQuery={lastCodeSearchQuery}
                    onGoToPreviousCodeSearchResult={
                      onGoToPreviousCodeSearchResult
                    }
                    onGoToNextCodeSearchResult={onGoToNextCodeSearchResult}
                    disabled={isDisabled || movement.isSelectingMoveTarget}
                  />
                )}
              </div>
            </div>
          </div>

          {isSearchEnabled && isSearchExpanded && (
            <div
              id="metadoc-toolbar-search-panel"
              className="metadoc-toolbar__search-panel"
            >
              <div className="metadoc-toolbar__search">
                {isSearchReady ? (
                  <>
                    <div className="metadoc-toolbar__search-input-wrapper">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(event) => {
                          setSearchQuery(event.target.value);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            if (navigateSearchResults(event.shiftKey)) {
                              event.preventDefault();
                              return;
                            }

                            event.preventDefault();
                            handleSearch();
                          }
                          if (event.key === "Escape") {
                            event.preventDefault();
                            closeSearchPanel();
                          }
                        }}
                        className="metadoc-toolbar__search-input"
                        placeholder="Search metadoc..."
                        disabled={isSearchDisabled}
                      />
                      {isSearching && (
                        <span
                          className="metadoc-toolbar__search-loading-spinner"
                          aria-label="Search in progress"
                        />
                      )}
                    </div>
                    {hasSearchResults && (
                      <div className="metadoc-toolbar__search-result-navigation">
                        <button
                          type="button"
                          onClick={onGoToPreviousSearchResult}
                          className="metadoc-toolbar__nav-button"
                          title="Previous search result"
                          aria-label="Previous search result"
                          disabled={
                            isSearchDisabled || !canGoToPreviousSearchResult
                          }
                        >
                          <span className="material-icons">chevron_left</span>
                        </button>
                        <button
                          type="button"
                          onClick={onGoToNextSearchResult}
                          className="metadoc-toolbar__nav-button"
                          title="Next search result"
                          aria-label="Next search result"
                          disabled={
                            isSearchDisabled || !canGoToNextSearchResult
                          }
                        >
                          <span className="material-icons">chevron_right</span>
                        </button>
                      </div>
                    )}
                    {lastSearchQuery && (
                      <span className="metadoc-toolbar__search-count">
                        {hasNavigableSearchResults &&
                        activeSearchResultIndex !== null
                          ? `${activeSearchResultIndex + 1}/${searchNavigableResultCount} shown (${searchResultCount} results)`
                          : `${searchResultCount} results`}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="metadoc-toolbar__search-status">
                    Search unavailable until the index is ready.
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    closeSearchPanel();
                  }}
                  className="metadoc-toolbar__nav-button metadoc-toolbar__search-close"
                  title="Close search"
                  aria-label="Close search"
                >
                  <span className="material-icons">close</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  },
);

MetaDocToolbar.displayName = "MetaDocToolbar";

export default MetaDocToolbar;
